import { openDB } from 'idb'
import { CATALOG_SCHEMA_VERSION, DB_NAME, DB_VERSION, META_KEYS, STORES } from './constants'

let dbPromise = null

/** Browser mendukung IndexedDB? (mode private beberapa browser menolak). */
export const isLocalDbSupported = () => typeof window !== 'undefined' && !!window.indexedDB

const CATALOG_META_KEYS = [
  META_KEYS.scopeEndpoint,
  META_KEYS.productsSyncedAt,
  META_KEYS.lastSyncedAt,
]

const clearCatalogInTx = async (tx) => {
  await tx.objectStore(STORES.PRODUCTS).clear()
  await tx.objectStore(STORES.PRICE_RULES).clear()
  const meta = tx.objectStore(STORES.META)
  await Promise.all(CATALOG_META_KEYS.map((key) => meta.delete(key)))
}

/** Versi mapping katalog berubah → bersihkan katalog lama (antrean tetap aman). */
const ensureCatalogSchema = async (db) => {
  const tx = db.transaction([STORES.META, STORES.PRODUCTS, STORES.PRICE_RULES], 'readwrite')
  const stored = await tx.objectStore(STORES.META).get(META_KEYS.schemaVersion)
  if (Number(stored?.value) !== CATALOG_SCHEMA_VERSION) {
    await clearCatalogInTx(tx)
    await tx.objectStore(STORES.META).put({
      key: META_KEYS.schemaVersion,
      value: String(CATALOG_SCHEMA_VERSION),
    })
  }
  await tx.done
}

/**
 * Singleton koneksi IndexedDB katalog + antrean. Dibuka lazily saat pertama
 * dipakai; reject bila browser tak mendukung / menolak (mode private).
 */
export const getLocalDb = () => {
  if (!dbPromise) {
    if (!isLocalDbSupported()) {
      return Promise.reject(new Error('Browser tidak mendukung penyimpanan lokal (IndexedDB)'))
    }
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORES.META)) {
          db.createObjectStore(STORES.META, { keyPath: 'key' })
        }
        if (!db.objectStoreNames.contains(STORES.PRODUCTS)) {
          db.createObjectStore(STORES.PRODUCTS, { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains(STORES.PRICE_RULES)) {
          db.createObjectStore(STORES.PRICE_RULES, { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains(STORES.ASYNC_OPS)) {
          const ops = db.createObjectStore(STORES.ASYNC_OPS, { keyPath: 'op_id' })
          ops.createIndex('status', 'status')
          ops.createIndex('created_at', 'created_at')
          ops.createIndex('batch_id', 'batch_id')
        }
        if (!db.objectStoreNames.contains(STORES.LOCAL_SEQ)) {
          db.createObjectStore(STORES.LOCAL_SEQ, { keyPath: 'key' })
        }
      },
    })
      .then(async (db) => {
        await ensureCatalogSchema(db)
        return db
      })
      .catch((error) => {
        // Izinkan percobaan buka ulang berikutnya (mis. setelah izin storage berubah).
        dbPromise = null
        throw error
      })
  }
  return dbPromise
}

/** Kosongkan seluruh data katalog (ganti tenant / reset). Antrean tidak disentuh. */
export const clearCatalog = async () => {
  const db = await getLocalDb()
  const tx = db.transaction([STORES.META, STORES.PRODUCTS, STORES.PRICE_RULES], 'readwrite')
  await clearCatalogInTx(tx)
  await tx.done
}

/**
 * Minta browser tidak menghapus data ini saat penyimpanan penuh. Kembalikan
 * true bila persist diberikan (atau sudah), false bila ditolak/tak didukung.
 */
export const requestPersistentStorage = async () => {
  try {
    if (!navigator?.storage?.persist) {
      return false
    }
    if (await navigator.storage.persisted?.()) {
      return true
    }
    return await navigator.storage.persist()
  } catch {
    return false
  }
}

/** Status persist saat ini (untuk peringatan di halaman Riwayat). */
export const isStoragePersisted = async () => {
  try {
    return !!(await navigator?.storage?.persisted?.())
  } catch {
    return false
  }
}
